import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import api from '../utils/axios';
import { toast } from 'sonner';
import {
  FileText, Download, BarChart3, Shield, Users, Clock,
  AlertTriangle, CheckCircle, XCircle, Loader2, TrendingUp,
  Calendar, Send, Plus, Trash2, Eye, RefreshCw,
  Mail, ChevronRight, Activity, Lock, Zap, Target, PieChart, Wallet
} from 'lucide-react';
import SaldosCuenta from '../components/reportes/SaldosCuenta';

const ESTADO_LABELS = {
  ABIERTO: 'Abierto', CERRADO: 'Cerrado', EN_REVISION: 'En Revisión', EN_INVESTIGACION: 'En Investigación',
  ESPERANDO_COMPLIANCE: 'Esp. Compliance', EN_VIGILANCIA: 'En Vigilancia', PENDIENTE_APROBACION: 'Pend. Aprobación',
};
const ESTADO_COLORS = {
  ABIERTO: '#3b82f6', CERRADO: '#10b981', EN_REVISION: '#8b5cf6', EN_INVESTIGACION: '#f59e0b',
  ESPERANDO_COMPLIANCE: '#ef4444', EN_VIGILANCIA: '#06b6d4', PENDIENTE_APROBACION: '#a855f7',
};

// ========== STAT CARD ==========
const Stat = ({ label, value, icon: Icon, color, sub }) => (
  <div className="relative overflow-hidden rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[10px] font-medium uppercase tracking-wider text-slate-400">{label}</p>
        <p className="mt-1 text-2xl font-extrabold" style={{ color: color || '#1e293b' }}>{value}</p>
        {sub && <p className="mt-0.5 text-[11px] text-slate-400">{sub}</p>}
      </div>
      {Icon && (
        <div className="rounded-lg p-2" style={{ backgroundColor: `${color || '#3b82f6'}15` }}>
          <Icon size={18} style={{ color: color || '#3b82f6' }} />
        </div>
      )}
    </div>
  </div>
);

// ========== HORIZONTAL BAR ==========
const HBar = ({ label, value, max, color, pct }) => (
  <div className="group flex items-center gap-3 py-1">
    <span className="w-44 shrink-0 text-right text-xs text-slate-600 leading-tight" title={label}>{label}</span>
    <div className="flex-1 relative h-6 bg-slate-100 rounded-md overflow-hidden">
      <div className="h-full rounded-md transition-all duration-500" style={{ width: `${max ? Math.max((value / max) * 100, 3) : 3}%`, background: `linear-gradient(90deg, ${color || '#6366f1'}, ${color || '#6366f1'}cc)` }} />
    </div>
    <span className="w-12 text-right text-xs font-bold tabular-nums" style={{ color: color || '#6366f1' }}>{value}</span>
    {pct !== undefined && <span className="w-12 text-right text-[10px] text-slate-400">{pct}%</span>}
  </div>
);

// ========== DONUT ==========
const MiniDonut = ({ segments, size = 100 }) => {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (!total) return null;
  let cum = 0;
  const r = 36, cx = 50, cy = 50, sw = 10;
  return (
    <svg viewBox="0 0 100 100" width={size} height={size}>
      {segments.map((seg, i) => {
        const pct = seg.value / total;
        const start = cum * 2 * Math.PI - Math.PI / 2;
        cum += pct;
        const end = cum * 2 * Math.PI - Math.PI / 2;
        const large = pct > 0.5 ? 1 : 0;
        const x1 = cx + r * Math.cos(start), y1 = cy + r * Math.sin(start);
        const x2 = cx + r * Math.cos(end), y2 = cy + r * Math.sin(end);
        return <path key={i} d={`M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`} fill="none" stroke={seg.color} strokeWidth={sw} />;
      })}
      <text x={cx} y={cy - 3} textAnchor="middle" className="text-lg font-bold" style={{ fontSize: '16px', fill: '#1e293b' }}>{total}</text>
      <text x={cx} y={cy + 10} textAnchor="middle" style={{ fontSize: '7px', fill: '#94a3b8' }}>total</text>
    </svg>
  );
};

// ========== SECTION HEADER ==========
const SectionHeader = ({ title, icon: Icon, color }) => (
  <div className="flex items-center gap-3 mb-5 pb-3 border-b border-slate-200">
    <div className="rounded-lg p-2.5" style={{ backgroundColor: `${color}15` }}>
      <Icon size={20} style={{ color }} />
    </div>
    <h3 className="text-lg font-bold text-slate-800">{title}</h3>
  </div>
);

// ========== SECCIÓN: DASHBOARD ==========
const RenderDashboard = ({ data }) => {
  const k = data.kpis || {};
  const topAlertas = Object.entries(data.top_alertas || {});
  const maxAlerta = topAlertas.length ? topAlertas[0][1] : 1;
  const totalAlertas = topAlertas.reduce((s, [, v]) => s + v, 0);
  const tendencia = Object.entries(data.tendencia_semanal || {});

  return (
    <div className="space-y-6">
      <SectionHeader title="Dashboard Resumen" icon={BarChart3} color="#3b82f6" />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Stat label="Total Casos" value={k.total || 0} icon={FileText} color="#1e293b" />
        <Stat label="Abiertos" value={k.abiertos || 0} icon={AlertTriangle} color="#3b82f6" />
        <Stat label="Bloqueados" value={k.bloqueados || 0} icon={Lock} color="#ef4444" />
        <Stat label="Score Promedio" value={k.score_promedio || 0} icon={Target} color={k.score_promedio >= 70 ? '#ef4444' : k.score_promedio >= 40 ? '#f59e0b' : '#10b981'} />
        <Stat label="Externos" value={k.externos || 0} icon={Users} color="#8b5cf6" />
      </div>

      {topAlertas.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-3">Top Alertas</p>
          <div className="space-y-0.5">
            {topAlertas.map(([name, count]) => (
              <HBar key={name} label={name} value={count} max={maxAlerta} color="#6366f1" pct={Math.round(count / totalAlertas * 100)} />
            ))}
          </div>
        </div>
      )}

      {tendencia.length > 1 && (
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-3">Tendencia Semanal</p>
          <div className="flex items-end gap-1.5 h-32 px-2">
            {tendencia.map(([semana, count]) => {
              const max = Math.max(...tendencia.map(([, v]) => v));
              const h = max ? (count / max) * 100 : 0;
              return (
                <div key={semana} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[10px] font-bold text-slate-600">{count}</span>
                  <div className="w-full rounded-t-md bg-gradient-to-t from-blue-500 to-blue-400 transition-all" style={{ height: `${Math.max(h, 5)}%` }} />
                  <span className="text-[9px] text-slate-400 -rotate-45 origin-top-left mt-1">{semana.replace('2026-', '')}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {tendencia.length <= 1 && tendencia.length > 0 && (
        <div className="p-3 bg-blue-50 rounded-lg text-center">
          <p className="text-sm text-blue-700"><strong>{tendencia[0]?.[1]}</strong> casos en <strong>{tendencia[0]?.[0]}</strong></p>
        </div>
      )}
      {data.por_analista && Object.keys(data.por_analista).length > 0 && (
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-3">Por Analista Asignado</p>
          <div className="space-y-1">
            {Object.entries(data.por_analista).map(([a, c]) => <HBar key={a} label={a} value={c} max={Math.max(...Object.values(data.por_analista))} color="#8b5cf6" pct={k.total ? Math.round(c / k.total * 100) : 0} />)}
          </div>
        </div>
      )}
    </div>
  );
};

// ========== SECCIÓN: GESTIÓN MENSUAL ==========
const RenderGestion = ({ data }) => {
  const estados = Object.entries(data.estados || {});
  const maxEstado = Math.max(...estados.map(([, v]) => v), 1);
  const scores = Object.entries(data.scores || {});

  const donutSegs = estados.map(([e, v]) => ({ value: v, color: ESTADO_COLORS[e] || '#94a3b8' }));

  return (
    <div className="space-y-6">
      <SectionHeader title="Gestión Mensual" icon={TrendingUp} color="#10b981" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total Casos" value={data.total_casos} icon={FileText} color="#1e293b" />
        <Stat label="Cerrados" value={data.cerrados} icon={CheckCircle} color="#10b981" sub={data.total_casos ? `${Math.round(data.cerrados / data.total_casos * 100)}% del total` : ''} />
        <Stat label="Bloqueados" value={data.bloqueados} icon={Lock} color="#ef4444" />
        <Stat label="Tiempo Resolución" value={data.tiempo_resolucion_promedio_horas ? `${data.tiempo_resolucion_promedio_horas}h` : 'N/A'} icon={Clock} color="#f59e0b" sub={data.tiempo_resolucion_promedio_horas ? `${Math.round(data.tiempo_resolucion_promedio_horas / 24)} días aprox.` : ''} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-3">Por Estado</p>
          <div className="flex items-center gap-6">
            <MiniDonut segments={donutSegs} size={110} />
            <div className="space-y-1.5 flex-1">
              {estados.map(([e, v]) => (
                <div key={e} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ESTADO_COLORS[e] || '#94a3b8' }} />
                    <span className="text-slate-600">{ESTADO_LABELS[e] || e}</span>
                  </div>
                  <span className="font-bold text-slate-800">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-3">Distribución de Score</p>
          <div className="space-y-1">
            {scores.map(([range, c]) => {
              const color = range === '80-100' ? '#ef4444' : range === '60-79' ? '#f97316' : range === '40-59' ? '#f59e0b' : range === '20-39' ? '#3b82f6' : '#94a3b8';
              return <HBar key={range} label={range} value={c} max={data.total_casos} color={color} pct={data.total_casos ? Math.round(c / data.total_casos * 100) : 0} />;
            })}
          </div>
        </div>
      </div>

      {data.por_mes && Object.keys(data.por_mes).length > 0 && (
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-3">Casos por Mes</p>
          <div className="space-y-1">
            {Object.entries(data.por_mes).map(([m, c]) => <HBar key={m} label={m} value={c} max={Math.max(...Object.values(data.por_mes))} color="#3b82f6" />)}
          </div>
        </div>
      )}

      {data.por_analista && Object.keys(data.por_analista).length > 0 && (
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-3">Por Analista Asignado</p>
          <div className="space-y-1">
            {Object.entries(data.por_analista).map(([a, c]) => <HBar key={a} label={a} value={c} max={Math.max(...Object.values(data.por_analista))} color="#8b5cf6" pct={data.total_casos ? Math.round(c / data.total_casos * 100) : 0} />)}
          </div>
        </div>
      )}
    </div>
  );
};

// ========== SECCIÓN: CARTERA ==========
const RenderCartera = ({ data }) => {
  const porEstado = Object.entries(data.por_estado || {});
  const donutSegs = porEstado.map(([e, v]) => ({ value: v, color: ESTADO_COLORS[e] || '#94a3b8' }));
  const analistas = Object.entries(data.por_analista || {});
  const maxAnalista = Math.max(...analistas.map(([, v]) => v), 1);

  return (
    <div className="space-y-6">
      <SectionHeader title="Estado de Cartera" icon={Users} color="#8b5cf6" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total" value={data.total_casos} icon={FileText} color="#1e293b" />
        <Stat label="Sin Asignar" value={data.sin_asignar} icon={AlertTriangle} color="#ef4444" sub={data.total_casos ? `${Math.round(data.sin_asignar / data.total_casos * 100)}%` : ''} />
        <Stat label="Abiertos" value={data.por_estado?.ABIERTO || 0} icon={Activity} color="#3b82f6" />
        <Stat label="Esp. Compliance" value={data.por_estado?.ESPERANDO_COMPLIANCE || 0} icon={Shield} color="#f59e0b" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-3">Distribución por Estado</p>
          <div className="flex items-center gap-6">
            <MiniDonut segments={donutSegs} size={110} />
            <div className="space-y-1.5 flex-1">
              {porEstado.map(([e, v]) => (
                <div key={e} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ESTADO_COLORS[e] || '#94a3b8' }} />
                    <span className="text-slate-600">{ESTADO_LABELS[e] || e}</span>
                  </div>
                  <span className="font-bold text-slate-800">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-3">Por Analista</p>
          <div className="space-y-1">
            {analistas.map(([a, c]) => <HBar key={a} label={a} value={c} max={maxAnalista} color="#8b5cf6" pct={data.total_casos ? Math.round(c / data.total_casos * 100) : 0} />)}
          </div>
        </div>
      </div>
    </div>
  );
};

// ========== SECCIÓN: BLOQUEOS ==========
const RenderBloqueos = ({ data }) => {
  const tasaColor = data.tasa_exito >= 90 ? '#10b981' : data.tasa_exito >= 70 ? '#f59e0b' : '#ef4444';
  return (
    <div className="space-y-6">
      <SectionHeader title="Bloqueos y Solicitudes" icon={Shield} color="#ef4444" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total Solicitudes" value={data.total_solicitudes} icon={FileText} color="#1e293b" />
        <Stat label="Tasa de Éxito" value={`${data.tasa_exito}%`} icon={Target} color={tasaColor} />
        <Stat label="Exitosos" value={data.exitosos} icon={CheckCircle} color="#10b981" />
        <Stat label="Errores" value={data.errores} icon={XCircle} color="#ef4444" />
      </div>

      {data.waya_estados && (
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-2">Estados en Waya</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(data.waya_estados).map(([e, c]) => {
              const bg = e === 'BLOQUEADO' ? 'bg-green-100 text-green-800 border-green-200' : e.includes('ERROR') ? 'bg-red-100 text-red-800 border-red-200' : e === 'VALIDANDO' ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-slate-100 text-slate-700 border-slate-200';
              return <Badge key={e} className={`${bg} border text-xs px-3 py-1`}>{e.replace(/_/g, ' ')}: <strong>{c}</strong></Badge>;
            })}
          </div>
        </div>
      )}

      {data.errores_detalle?.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-2">Detalle de Errores</p>
          <div className="space-y-1.5">
            {data.errores_detalle.map((e, i) => (
              <div key={i} className="p-3 bg-red-50 rounded-lg border border-red-200 flex items-start gap-3">
                <XCircle size={16} className="text-red-400 mt-0.5 shrink-0" />
                <div className="text-xs">
                  <p className="font-semibold text-red-800">{e.entidad || 'Cuenta sin identificar'}</p>
                  <p className="text-red-600 mt-0.5">{(e.error || '').replace(/Page\.goto:.*$/, 'Error de navegación en Waya').replace(/Call log:.*$/, '').substring(0, 120)}</p>
                  {e.fecha && <p className="text-red-400 mt-1 text-[10px]">{new Date(e.fecha).toLocaleDateString('es-AR')}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ========== SECCIÓN: MONITORES ==========
const RenderMonitores = ({ data }) => {
  const fp = Object.entries(data.falsos_positivos || {});
  const maxFP = fp.length ? Math.max(...fp.map(([, v]) => v)) : 1;

  return (
    <div className="space-y-6">
      <SectionHeader title="Eficiencia de Monitores" icon={Zap} color="#f59e0b" />
      <Stat label="Total Alertas Detectadas" value={data.total_alertas} icon={AlertTriangle} color="#f59e0b" />

      {data.stats?.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="p-3 text-left font-semibold text-slate-600">Monitor</th>
                <th className="p-3 text-right font-semibold text-slate-600">Detecciones</th>
                <th className="p-3 text-right font-semibold text-slate-600">Casos</th>
                <th className="p-3 text-right font-semibold text-slate-600">Score Prom.</th>
                <th className="p-3 text-left font-semibold text-slate-600">Severidad</th>
              </tr>
            </thead>
            <tbody>
              {data.stats.map((m, i) => (
                <tr key={i} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="p-3 font-medium text-slate-800">{m.nombre}</td>
                  <td className="p-3 text-right font-bold text-slate-700">{m.detecciones}</td>
                  <td className="p-3 text-right text-slate-600">{m.casos}</td>
                  <td className="p-3 text-right">
                    <span className="font-bold" style={{ color: m.score_promedio >= 80 ? '#ef4444' : m.score_promedio >= 50 ? '#f59e0b' : '#3b82f6' }}>{m.score_promedio}</span>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      {Object.entries(m.severidades || {}).map(([sev, cnt]) => (
                        <Badge key={sev} className={`text-[9px] ${sev === 'CRITICA' || sev === 'MUY_ALTA' ? 'bg-red-100 text-red-700' : sev === 'ALTA' ? 'bg-orange-100 text-orange-700' : sev === 'MEDIA' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                          {sev}: {cnt}
                        </Badge>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {fp.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-3">Posibles Falsos Positivos <span className="font-normal text-slate-400">(casos cerrados por monitor)</span></p>
          <div className="space-y-1">
            {fp.map(([m, c]) => <HBar key={m} label={m} value={c} max={maxFP} color="#f97316" />)}
          </div>
        </div>
      )}
    </div>
  );
};

// ========== SECCIÓN: REGULATORIO ==========
const RenderRegulatorio = ({ data }) => (
  <div className="space-y-6">
    <SectionHeader title="Regulatorio Periódico" icon={Shield} color="#6366f1" />
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      <Stat label="Total Período" value={data.total_periodo} icon={FileText} color="#1e293b" />
      <Stat label="Cuentas Bloqueadas" value={data.cuentas_bloqueadas} icon={Lock} color="#ef4444" />
      <Stat label="Esp. Compliance" value={data.esperando_compliance} icon={Shield} color="#f59e0b" />
    </div>
    {data.detalle_bloqueados?.length > 0 && (
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="p-3 text-left font-semibold text-slate-600">CUIT/CVU</th>
              <th className="p-3 text-left font-semibold text-slate-600">Nombre</th>
              <th className="p-3 text-right font-semibold text-slate-600">Score</th>
              <th className="p-3 text-right font-semibold text-slate-600">Alertas</th>
              <th className="p-3 text-left font-semibold text-slate-600">Fecha Bloqueo</th>
            </tr>
          </thead>
          <tbody>
            {data.detalle_bloqueados.map((b, i) => (
              <tr key={i} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="p-3 font-mono text-[10px] text-slate-600">{b.cuit}</td>
                <td className="p-3 font-medium text-slate-800">{b.nombre || <span className="text-slate-300 italic">Sin resolver</span>}</td>
                <td className="p-3 text-right">
                  <span className="font-bold" style={{ color: (b.score || 0) >= 80 ? '#ef4444' : '#f59e0b' }}>{b.score}</span>
                </td>
                <td className="p-3 text-right text-slate-700">{b.alertas}</td>
                <td className="p-3 text-slate-600">{b.fecha_bloqueo ? new Date(b.fecha_bloqueo).toLocaleDateString('es-AR') : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const RENDERERS = { dashboard: RenderDashboard, gestion_mensual: RenderGestion, cartera: RenderCartera, bloqueos: RenderBloqueos, monitores: RenderMonitores, regulatorio: RenderRegulatorio };
const SEC_ICONS = { dashboard: BarChart3, gestion_mensual: TrendingUp, cartera: Users, bloqueos: Shield, monitores: Zap, regulatorio: FileText };
const SEC_COLORS = { dashboard: '#3b82f6', gestion_mensual: '#10b981', cartera: '#8b5cf6', bloqueos: '#ef4444', monitores: '#f59e0b', regulatorio: '#6366f1' };

// ========== MAIN ==========
export default function Reportes() {
  const [tab, setTab] = useState('generar');
  const [secciones, setSecciones] = useState([]);
  const [sel, setSel] = useState({});
  const [filtros, setFiltros] = useState({ desde: '', hasta: '', estado: '', prioridad: '', titulo: '' });
  const [generando, setGenerando] = useState(false);
  const [reporte, setReporte] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [programados, setProgramados] = useState([]);
  const [showProg, setShowProg] = useState(false);
  const [progForm, setProgForm] = useState({ nombre: '', frecuencia: 'semanal', destinatarios: '', periodo_tipo: 'ultimo_mes' });

  useEffect(() => { loadSecciones(); loadHistorial(); loadProgramados(); }, []);

  const loadSecciones = async () => { try { const r = await api.get('/reportes/secciones'); setSecciones(r.data.secciones || []); const s = {}; (r.data.secciones || []).forEach(x => { s[x.id] = true; }); setSel(s); } catch {} };
  const loadHistorial = async () => { try { const r = await api.get('/reportes/historial'); setHistorial(r.data.reportes || []); } catch {} };
  const loadProgramados = async () => { try { const r = await api.get('/reportes/programados'); setProgramados(r.data.reportes || []); } catch {} };

  const [secConfigs, setSecConfigs] = useState({});
  const [expandedSec, setExpandedSec] = useState(null);
  const ALL_ESTADOS = ['ABIERTO', 'EN_INVESTIGACION', 'ESPERANDO_COMPLIANCE', 'EN_VIGILANCIA', 'CERRADO', 'PENDIENTE_APROBACION'];

  const getSecConfig = (id) => secConfigs[id] || { estados: [], mostrar_asignacion: false };
  const updateSecConfig = (id, field, value) => setSecConfigs({ ...secConfigs, [id]: { ...getSecConfig(id), [field]: value } });
  const toggleEstado = (secId, estado) => {
    const cfg = getSecConfig(secId);
    const estados = cfg.estados || [];
    const next = estados.includes(estado) ? estados.filter(e => e !== estado) : [...estados, estado];
    updateSecConfig(secId, 'estados', next);
  };

  const generar = async () => {
    const ids = Object.entries(sel).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length) { toast.warning('Seleccioná al menos una sección'); return; }
    setGenerando(true);
    try {
      const secciones_config = ids.map(id => {
        const cfg = getSecConfig(id);
        return { id, estados: cfg.estados?.length ? cfg.estados : null, mostrar_asignacion: cfg.mostrar_asignacion };
      });
      const r = await api.post('/reportes/generar-combinado', {
        secciones_config, periodo_desde: filtros.desde || undefined, periodo_hasta: filtros.hasta || undefined,
        filtro_prioridad: filtros.prioridad || undefined, titulo_personalizado: filtros.titulo || undefined,
      });
      setReporte(r.data);
      toast.success(`Reporte generado: ${r.data.total_casos_analizados} casos`);
      loadHistorial();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setGenerando(false); }
  };

  const programar = async () => {
    const ids = Object.entries(sel).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length || !progForm.nombre || !progForm.destinatarios) { toast.warning('Completá todos los campos'); return; }
    try {
      await api.post('/reportes/programar', { nombre: progForm.nombre, secciones: ids, frecuencia: progForm.frecuencia, destinatarios: progForm.destinatarios.split(',').map(e => e.trim()).filter(Boolean), periodo_tipo: progForm.periodo_tipo });
      toast.success('Programado'); setShowProg(false); loadProgramados();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const selCount = Object.values(sel).filter(Boolean).length;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="reportes-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Reportes</h1>
        <p className="text-sm text-slate-500">Informes combinados con datos reales de tu operación</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="generar">Generar</TabsTrigger>
          <TabsTrigger value="historial">Historial ({historial.length})</TabsTrigger>
          <TabsTrigger value="programados">Programados ({programados.length})</TabsTrigger>
          <TabsTrigger value="saldos" data-testid="tab-saldos"><Wallet size={14} className="mr-1.5" /> Saldos en Cuenta</TabsTrigger>
        </TabsList>

        <TabsContent value="generar">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1 space-y-4">
              <Card className="p-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-bold text-slate-800">Secciones</h3>
                  <Button variant="ghost" size="sm" className="text-[10px] h-6"
                    onClick={() => { const a = {}; secciones.forEach(s => { a[s.id] = !Object.values(sel).every(Boolean); }); setSel(a); }}>
                    {Object.values(sel).every(Boolean) ? 'Ninguna' : 'Todas'}
                  </Button>
                </div>
                {secciones.map(s => {
                  const Icon = SEC_ICONS[s.id] || FileText;
                  const isExpanded = expandedSec === s.id;
                  const cfg = getSecConfig(s.id);
                  const hasCustom = (cfg.estados?.length > 0) || cfg.mostrar_asignacion;
                  return (
                    <div key={s.id} className={`rounded-lg border transition-all mb-1.5 ${sel[s.id] ? 'bg-blue-50/80 border-blue-200 shadow-sm' : 'bg-white border-slate-200'}`}>
                      <label className="flex items-center gap-2.5 p-2.5 cursor-pointer">
                        <input type="checkbox" checked={!!sel[s.id]} onChange={e => setSel({ ...sel, [s.id]: e.target.checked })} className="rounded" />
                        <div className="rounded-md p-1.5" style={{ backgroundColor: `${SEC_COLORS[s.id]}15` }}>
                          <Icon size={14} style={{ color: SEC_COLORS[s.id] }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-800">{s.nombre}</p>
                          <p className="text-[10px] text-slate-400 truncate">{s.descripcion}</p>
                        </div>
                        {sel[s.id] && (
                          <button type="button" onClick={e => { e.preventDefault(); setExpandedSec(isExpanded ? null : s.id); }}
                            className={`p-1 rounded hover:bg-slate-200 transition-colors ${hasCustom ? 'text-blue-500' : 'text-slate-400'}`} title="Configurar filtros">
                            <ChevronRight size={12} className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          </button>
                        )}
                      </label>
                      {isExpanded && sel[s.id] && (
                        <div className="px-3 pb-3 pt-1 border-t border-blue-100 space-y-2">
                          <div>
                            <p className="text-[10px] text-slate-500 uppercase mb-1">Estados a incluir <span className="text-slate-300">(vacío = todos)</span></p>
                            <div className="flex flex-wrap gap-1">
                              {ALL_ESTADOS.map(est => (
                                <button key={est} type="button"
                                  onClick={() => toggleEstado(s.id, est)}
                                  className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${cfg.estados?.includes(est) ? 'text-white border-transparent' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}
                                  style={cfg.estados?.includes(est) ? { backgroundColor: ESTADO_COLORS[est] || '#94a3b8', borderColor: ESTADO_COLORS[est] } : {}}>
                                  {ESTADO_LABELS[est] || est}
                                </button>
                              ))}
                            </div>
                          </div>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={cfg.mostrar_asignacion} onChange={e => updateSecConfig(s.id, 'mostrar_asignacion', e.target.checked)} className="rounded" />
                            <span className="text-[10px] text-slate-600">Desglosar por analista asignado</span>
                          </label>
                        </div>
                      )}
                    </div>
                  );
                })}
              </Card>
              <Card className="p-4">
                <h3 className="text-sm font-bold text-slate-800 mb-3">Filtros</h3>
                <div className="space-y-2.5">
                  <div><label className="text-[10px] text-slate-500 uppercase">Título</label><Input value={filtros.titulo} onChange={e => setFiltros({ ...filtros, titulo: e.target.value })} placeholder="Reporte AlertGuard" className="h-8 text-xs" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="text-[10px] text-slate-500 uppercase">Desde</label><Input type="date" value={filtros.desde} onChange={e => setFiltros({ ...filtros, desde: e.target.value })} className="h-8 text-xs" /></div>
                    <div><label className="text-[10px] text-slate-500 uppercase">Hasta</label><Input type="date" value={filtros.hasta} onChange={e => setFiltros({ ...filtros, hasta: e.target.value })} className="h-8 text-xs" /></div>
                  </div>
                  <div><label className="text-[10px] text-slate-500 uppercase">Prioridad</label>
                    <select value={filtros.prioridad} onChange={e => setFiltros({ ...filtros, prioridad: e.target.value })} className="w-full h-8 text-xs rounded border border-slate-200 px-2 bg-white">
                      <option value="">Todas</option><option value="CRITICA">Crítica</option><option value="ALTA">Alta</option><option value="MEDIA">Media</option><option value="BAJA">Baja</option>
                    </select>
                  </div>
                </div>
              </Card>
              <Button onClick={generar} disabled={generando || !selCount} className="bg-blue-600 hover:bg-blue-700 text-white w-full" data-testid="generar-reporte-btn">
                {generando ? <><Loader2 size={14} className="animate-spin mr-1" /> Generando...</> : <><Eye size={14} className="mr-1" /> Generar ({selCount})</>}
              </Button>
              <Button variant="outline" onClick={() => setShowProg(true)} disabled={!selCount} className="w-full text-xs">
                <Mail size={14} className="mr-1" /> Programar envío
              </Button>
            </div>

            <div className="lg:col-span-3">
              {!reporte ? (
                <Card className="p-16 text-center border-dashed border-2">
                  <PieChart size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500 text-sm">Seleccioná las secciones y clickeá <strong>Generar</strong></p>
                </Card>
              ) : (
                <div className="space-y-6" data-testid="reporte-preview">
                  <Card className="p-5 bg-gradient-to-r from-slate-800 to-slate-700 text-white border-0">
                    <div className="flex justify-between items-center">
                      <div>
                        <h2 className="text-lg font-bold">{reporte.titulo}</h2>
                        <p className="text-xs text-slate-300 mt-1">
                          {reporte.generado_por} | {new Date(reporte.generado_at).toLocaleString('es-AR')} | {reporte.total_casos_analizados} casos
                        </p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => window.print()} className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                        <Download size={14} className="mr-1" /> Exportar
                      </Button>
                    </div>
                  </Card>
                  {Object.entries(reporte.secciones || {}).map(([sid, data]) => {
                    const R = RENDERERS[sid];
                    return R ? <Card key={sid} className="p-6" data-testid={`seccion-${sid}`}><R data={data} /></Card> : null;
                  })}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="historial">
          {historial.length === 0 ? <Card className="p-8 text-center text-slate-400">Sin reportes generados</Card> : (
            <div className="space-y-2">
              {historial.map((r, i) => (
                <Card key={i} className="p-4 flex justify-between items-center hover:shadow-sm transition-shadow">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{r.titulo}</p>
                    <p className="text-xs text-slate-500">{new Date(r.generado_at).toLocaleString('es-AR')} | {r.generado_por} | {r.total_casos_analizados} casos</p>
                    <div className="flex gap-1 mt-1">{(r.secciones_ids || []).map(sid => <Badge key={sid} className="text-[9px] bg-slate-100 text-slate-600">{sid}</Badge>)}</div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="programados">
          {programados.length === 0 ? (
            <Card className="p-8 text-center text-slate-400">
              <p>Sin reportes programados</p>
              <Button variant="outline" size="sm" className="mt-2" onClick={() => { setTab('generar'); setShowProg(true); }}><Plus size={14} className="mr-1" /> Crear</Button>
            </Card>
          ) : (
            <div className="space-y-2">
              {programados.map((p, i) => (
                <Card key={i} className="p-4 flex justify-between items-center">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{p.nombre}</p>
                    <p className="text-xs text-slate-500">{p.frecuencia} | {(p.destinatarios || []).join(', ')}</p>
                    <div className="flex gap-1 mt-1">{(p.secciones || []).map(sid => <Badge key={sid} className="text-[9px] bg-slate-100 text-slate-600">{sid}</Badge>)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={p.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>{p.activo ? 'Activo' : 'Pausado'}</Badge>
                    <Button variant="ghost" size="sm" onClick={async () => { if (window.confirm('Eliminar?')) { try { await api.delete(`/reportes/programados/${p.id}`); loadProgramados(); } catch {} } }} className="text-red-400"><Trash2 size={14} /></Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="saldos">
          <SaldosCuenta />
        </TabsContent>
      </Tabs>

      <Dialog open={showProg} onOpenChange={setShowProg}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Programar envío</DialogTitle><DialogDescription>Enviará automáticamente el reporte con las secciones seleccionadas</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-slate-500 uppercase">Nombre</label><Input value={progForm.nombre} onChange={e => setProgForm({ ...progForm, nombre: e.target.value })} placeholder="Reporte semanal" className="h-8 text-sm" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs text-slate-500 uppercase">Frecuencia</label><select value={progForm.frecuencia} onChange={e => setProgForm({ ...progForm, frecuencia: e.target.value })} className="w-full h-8 text-sm rounded border px-2 bg-white"><option value="diario">Diario</option><option value="semanal">Semanal</option><option value="mensual">Mensual</option></select></div>
              <div><label className="text-xs text-slate-500 uppercase">Período</label><select value={progForm.periodo_tipo} onChange={e => setProgForm({ ...progForm, periodo_tipo: e.target.value })} className="w-full h-8 text-sm rounded border px-2 bg-white"><option value="ultimo_dia">Último día</option><option value="ultima_semana">Última semana</option><option value="ultimo_mes">Último mes</option><option value="todo">Todo</option></select></div>
            </div>
            <div><label className="text-xs text-slate-500 uppercase">Destinatarios (separados por coma)</label><Input value={progForm.destinatarios} onChange={e => setProgForm({ ...progForm, destinatarios: e.target.value })} placeholder="ana@empresa.com, juan@empresa.com" className="h-8 text-sm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProg(false)}>Cancelar</Button>
            <Button onClick={programar} className="bg-blue-600 hover:bg-blue-700 text-white"><Mail size={14} className="mr-1" /> Programar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
